import React, { useMemo, useRef } from 'react'
import type { ExtraProps } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { AgentResourceCreationAnnotation } from 'features/ask-yogi/components/AgentResourceCreationAnnotation'
import { AskUserInputWidget } from 'features/ask-yogi/components/AskUserInputWidget'
import Chart from 'features/ask-yogi/features/conversation/components/Chart'
import Citation, {
  CitationProps,
} from 'features/ask-yogi/features/conversation/components/Citation'
import { computeColumnAligns } from 'features/ask-yogi/features/conversation/components/message-content/column-align'
import {
  ChartContainer,
  MessageContentWrapper,
  StyledOl,
  StyledTable,
  StyledUl,
  TableContainer,
  TableScrollWrapper,
} from 'features/ask-yogi/features/conversation/components/message-content/message-content-styles'
import { reconstructTableMarkdown } from 'features/ask-yogi/features/conversation/components/message-content/table-clipboard'
import TableToolbar from 'features/ask-yogi/features/conversation/components/message-content/TableToolbar/TableToolbar'
import MessageActions from 'features/ask-yogi/features/conversation/components/MessageActions'
import PageChart from 'features/ask-yogi/features/conversation/components/PageChart/PageChart'
import { ResourceCard } from 'features/ask-yogi/features/conversation/components/ResourceCard/ResourceCard'
import VerbatimCard, {
  VerbatimCardProps,
} from 'features/ask-yogi/features/conversation/components/VerbatimCard/VerbatimCard'
import WebCitationPill from 'features/ask-yogi/features/conversation/components/web-citation-pill/WebCitationPill'
import { useCitationData } from 'features/ask-yogi/features/conversation/hooks/use-citation-data'
import { useCitationsData } from 'features/ask-yogi/features/conversation/hooks/use-citations-data'
import type { ConversationExportFormat } from 'features/ask-yogi/features/conversation/hooks/use-conversation-export'
import {
  FeedbackData,
  useFeedbackMutation,
} from 'features/ask-yogi/features/conversation/hooks/use-feedback-mutation'
import { usePostTextTheme } from 'features/ask-yogi/features/conversation/hooks/use-post-text-theme'
import type { AskYogiChartExportHandle } from 'features/ask-yogi/features/conversation/utils/capture-chart-slides'
import type { ChartData } from 'features/ask-yogi/types'
import type { Message } from 'features/ask-yogi/types'
import type { ProcessedParts } from 'features/ask-yogi/utils/message-processing'
import { chartFactory } from 'features/charts-v2/chart-factory'
import type { HandedOffChart } from 'features/charts-v2/types/chart-handoff'
import type { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

const CitationWithData: React.FC<
  Omit<CitationProps, 'citationData' | 'isLoading'>
> = (props) => {
  // Reads from the cache seeded by the message-level `useCitationsData`
  // batch. `undefined` means the batch is still in flight (spinner);
  // `null` means it resolved without this id (fallback).
  const { data } = useCitationData(props.projectId, props.citationId)
  return (
    <Citation {...props} citationData={data} isLoading={data === undefined} />
  )
}

const VerbatimCardConnected: React.FC<
  Omit<VerbatimCardProps, 'post' | 'theme' | 'isLoading'>
> = (props) => {
  // Same cache the inline citation pill reads (seeded once per message by
  // useCitationsData), plus the sentence-theme cache when this verbatim
  // named a post_text_id.
  const { data: post } = useCitationData(props.projectId, props.verbatim.postId)
  const { data: theme } = usePostTextTheme(
    props.projectId,
    props.verbatim.postTextId,
  )
  return (
    <VerbatimCard
      {...props}
      post={post}
      theme={theme}
      isLoading={post === undefined}
    />
  )
}

const MessageActionsWithFeedback: React.FC<{
  message: { content: string; uuid?: string }
  index: number
  projectId: string
  conversationUuid?: string
  createTime?: string
  onExportConversation?: (format: ConversationExportFormat) => void
}> = ({ projectId, ...rest }) => {
  const feedbackMutation = useFeedbackMutation(projectId)
  const handleSubmitFeedback = async (data: FeedbackData): Promise<void> => {
    await feedbackMutation.mutateAsync(data)
  }
  return (
    <MessageActions
      {...rest}
      projectId={projectId}
      onSubmitFeedback={handleSubmitFeedback}
    />
  )
}

// Type for markdown component props - allows data attributes
type MarkdownComponentProps = React.HTMLAttributes<HTMLElement> &
  ExtraProps & {
    'data-citation-id'?: string
    'data-citation-index'?: string
    'data-wc-url'?: string
    'data-wc-title'?: string
    'data-wc-source'?: string
  }

export interface MessageContentProps {
  processedParts: ProcessedParts
  message: Message
  messageIndex: number
  projectId: string
  citationFilters?: { [recordId: string]: string | null }
  conversationUuid?: string
  compact?: boolean
  showActions?: boolean
  isSharedView?: boolean
  onTableFilterClick?: (resultIds: string[]) => void
  // Sends the clarifying-question form's selections back as a normal chat
  // message (readable "<header>: <value>" lines). Absent in read-only /
  // shared views, which renders the form disabled.
  onAskUserQuestionSubmit?: (content: string) => void
  // True only when this message is the last in the conversation. A
  // clarifying-question form is answerable only while it is the latest message;
  // once any turn follows it, the form must render read-only. This is the
  // durable gate that survives reload (local submitted state does not).
  isLatestMessage?: boolean
  onExportConversation?: (format: ConversationExportFormat) => void
  // Registry for the "Export entire convo → PDF" batch chart capture — every
  // rendered chart artifact registers its imperative export handle here,
  // keyed by artifact id, via a plain ref callback (see capture-chart-slides.ts).
  chartRefsMapRef?: React.MutableRefObject<
    Record<string, AskYogiChartExportHandle | undefined>
  >
  // The chart just handed off to the charts page via "View in charts", if
  // any — the matching chart (by message + chart key) renders as a
  // collapsed `ResourceCard` accordion instead of the full chart (PROD-5795).
  collapsedChart?: HandedOffChart | null
}

/**
 * A collapsed chart's label: its title, else what the charts page calls a
 * panel with no title and no template — its chart type's own name, the last
 * fallback of `resolvePanelTitle` — else "Chart". A chart a charts-page turn
 * wrote carries a title only when the user typed one.
 */
const chipTitle = (artifact: {
  title?: string
  request: Record<string, unknown>
}): string => {
  if (artifact.title) return artifact.title
  const panel = artifact.request['panel'] as
    | { chartType?: ChartTypeV2 }
    | undefined
  const chartType = panel?.chartType
  return (
    (chartType && chartFactory.getStrategy(chartType)?.getTitle()) || 'Chart'
  )
}

const MessageContent: React.FC<MessageContentProps> = React.memo(
  ({
    processedParts,
    message,
    messageIndex,
    projectId,
    citationFilters = {},
    conversationUuid,
    compact = false,
    showActions = true,
    isSharedView = false,
    onTableFilterClick,
    onAskUserQuestionSubmit,
    isLatestMessage = false,
    onExportConversation,
    chartRefsMapRef,
    collapsedChart,
  }) => {
    const tableCounterRef = useRef<number>(0)
    const columnAlignsRef = useRef<('left' | 'right')[]>([])
    const colIndexRef = useRef<number>(0)

    // Batch-fetch every citation's preview (and any named sentence's
    // theme/sentiment) for this message in one request, seeding the caches
    // <CitationWithData> and <VerbatimCardConnected> read from.
    useCitationsData(
      projectId,
      processedParts.citationIds,
      processedParts.postTextIds,
    )

    // Reset table counter on each render for this message
    tableCounterRef.current = 0

    const {
      parts,
      charts,
      chartArtifactIds,
      annotationArtifactIds,
      verbatims,
      tables,
    } = processedParts

    // Process children for slash delimiter in table headers
    const processChildren = (child: React.ReactNode): React.ReactNode => {
      if (typeof child === 'string' && child.includes('/')) {
        const splitParts = child.split('/')
        return splitParts.map((part, index) => (
          <span key={index}>
            {part.trim()}
            {index < splitParts.length - 1 && (
              <>
                /<br />
              </>
            )}
          </span>
        ))
      }
      return child
    }
    const markdownComponents = useMemo(
      () => ({
        pre: ({ ...props }: MarkdownComponentProps) => (
          <pre style={{ whiteSpace: 'pre-wrap' }} {...props} />
        ),
        table: ({ node, ...props }: MarkdownComponentProps) => {
          const currentTableIndex = tableCounterRef.current++
          const tableInfo = tables[currentTableIndex]
          // Raw markdown reconstructed from the mdast so every rendered
          // table (tagged or untagged) gets a Copy button. The upstream
          // tableMarkdown[] array only tracks `<table results="…">` blocks;
          // untagged summary tables the model composes inline have no
          // matching entry.
          const rawMarkdown = reconstructTableMarkdown(node as never)
          const hasResultIds =
            !!tableInfo?.resultIds?.length && !!onTableFilterClick
          const handleFilterClick = hasResultIds
            ? () => onTableFilterClick!(tableInfo!.resultIds)
            : undefined

          columnAlignsRef.current = computeColumnAligns(node)

          return (
            <TableContainer $compact={compact}>
              <TableToolbar
                tableMarkdown={rawMarkdown}
                title={tableInfo?.title}
                onFilterClick={handleFilterClick}
                compact={compact}
                messageUuid={message.uuid}
                conversationUuid={conversationUuid}
                tableIndex={currentTableIndex}
              />
              <TableScrollWrapper $compact={compact}>
                <StyledTable $compact={compact} {...props} />
              </TableScrollWrapper>
            </TableContainer>
          )
        },
        tr: ({ node: _node, ...props }: MarkdownComponentProps) => {
          colIndexRef.current = 0
          return <tr {...props} />
        },
        th: ({ node: _node, ...props }: MarkdownComponentProps) => {
          const { children, ...restProps } = props
          // Advance index so td's per-row counter stays in sync; headers
          // themselves always left-align for a uniform top edge.
          colIndexRef.current++

          const processedChildContent = Array.isArray(children)
            ? children.map(processChildren)
            : processChildren(children)

          return <th {...restProps}>{processedChildContent}</th>
        },
        td: ({ node: _node, ...props }: MarkdownComponentProps) => {
          const colIndex = colIndexRef.current++
          const align = columnAlignsRef.current[colIndex] ?? 'left'
          return <td data-align={align} {...props} />
        },
        h1: ({ ...props }: MarkdownComponentProps) => <h1 {...props} />,
        h2: ({ ...props }: MarkdownComponentProps) => <h2 {...props} />,
        h3: ({ ...props }: MarkdownComponentProps) => <h3 {...props} />,
        p: ({ ...props }: MarkdownComponentProps) => <p {...props} />,
        ul: ({ node: _node, ...props }: MarkdownComponentProps) => (
          <StyledUl $compact={compact} {...props} />
        ),
        ol: ({ node: _node, ...props }: MarkdownComponentProps) => (
          <StyledOl $compact={compact} {...props} />
        ),
        span: ({ ...props }: MarkdownComponentProps) => {
          const citationId = props['data-citation-id']
          const citationIndex = props['data-citation-index']
          if (citationId && citationIndex) {
            return (
              <CitationWithData
                projectId={projectId}
                citationId={citationId}
                index={parseInt(citationIndex)}
                message_uuid={message.uuid}
                conversation_uuid={conversationUuid}
                filterHash={citationFilters[citationId] || undefined}
              />
            )
          }
          const wcUrl = props['data-wc-url']
          if (wcUrl) {
            return (
              <WebCitationPill
                url={wcUrl}
                title={props['data-wc-title'] ?? ''}
                source={props['data-wc-source'] ?? ''}
              />
            )
          }
          return <span {...props} />
        },
        a: ({ node: _node, ...props }: MarkdownComponentProps) => (
          <a target="_blank" rel="noopener noreferrer" {...props} />
        ),
      }),
      [
        compact,
        tables,
        onTableFilterClick,
        message.uuid,
        conversationUuid,
        citationFilters,
        projectId,
      ],
    )

    return (
      <MessageContentWrapper $compact={compact}>
        {parts.map((part: string, partIndex: number) => (
          <React.Fragment key={partIndex}>
            {part && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {part}
              </ReactMarkdown>
            )}
            {verbatims[partIndex]
              ? // A verbatim blockquote+citation resolved to a card — see
                // message-processing.ts's extractVerbatimBlocks.
                (() => {
                  const verbatim = verbatims[partIndex]!
                  return (
                    <VerbatimCardConnected
                      verbatim={verbatim}
                      projectId={projectId}
                      compact={compact}
                      tracking={{
                        messageUuid: message.uuid,
                        conversationUuid,
                        filterHash:
                          citationFilters[verbatim.postId] || undefined,
                      }}
                    />
                  )
                })()
              : chartArtifactIds[partIndex]
                ? // New artifact format: Chart fetches its own data by request
                  (() => {
                    const artifactId = chartArtifactIds[partIndex]
                    const artifact = message.artifacts?.charts.find(
                      (c) => c.result_id === artifactId,
                    )
                    if (!artifact) return null
                    const isCollapsed =
                      artifact.collapsed === true ||
                      (collapsedChart?.messageUuid === message.uuid &&
                        collapsedChart?.chartKey === artifact.result_id)
                    const registerChart = (
                      handle: AskYogiChartExportHandle | null,
                    ): void => {
                      if (!chartRefsMapRef) return
                      chartRefsMapRef.current[artifact.result_id] =
                        handle ?? undefined
                    }
                    // A charts-page turn's chart stands for the whole page it
                    // left; older turns' charts each stand for one panel.
                    const chart = artifact.page?.panels.length ? (
                      <PageChart
                        ref={registerChart}
                        page={artifact.page}
                        conversationUuid={conversationUuid}
                        messageUuid={message.uuid || ''}
                        chartKey={artifact.result_id}
                      />
                    ) : (
                      <Chart
                        ref={registerChart}
                        request={artifact.request}
                        title={artifact.title}
                        conversationUuid={conversationUuid}
                        messageUuid={message.uuid || ''}
                        chartKey={artifact.result_id}
                      />
                    )
                    return (
                      <ChartContainer $compact={compact}>
                        {isCollapsed ? (
                          <ResourceCard
                            variant="accordion"
                            icon="chart"
                            title={chipTitle(artifact)}
                            subtitle={artifact.subtitle ?? 'Chart'}
                            defaultExpanded={false}
                          >
                            {chart}
                          </ResourceCard>
                        ) : (
                          chart
                        )}
                      </ChartContainer>
                    )
                  })()
                : annotationArtifactIds[partIndex]
                  ? // Annotation artifact: inline link to a created resource
                    (() => {
                      const annotationId = annotationArtifactIds[partIndex]
                      const annotation = message.artifacts?.annotations?.find(
                        (a) => a.id === annotationId,
                      )
                      return annotation ? (
                        <AgentResourceCreationAnnotation
                          annotation={annotation}
                          projectId={projectId}
                          conversationId={
                            isSharedView ? undefined : conversationUuid
                          }
                        />
                      ) : null
                    })()
                  : // Legacy format: data pre-loaded in message.charts
                    charts[partIndex] &&
                    message.charts &&
                    message.charts[partIndex] && (
                      <ChartContainer $compact={compact}>
                        {(() => {
                          const legacyChart = (
                            <Chart
                              data={message.charts[partIndex].data as never}
                              identifier={
                                message.charts[partIndex]
                                  .identifier as unknown as Record<
                                  string,
                                  unknown
                                >
                              }
                              conversationUuid={conversationUuid}
                              messageUuid={message.uuid || ''}
                              chartKey={String(partIndex)}
                              filters={
                                (
                                  message.charts[partIndex] as ChartData & {
                                    filters?: Array<{
                                      field: string
                                      values?: (string | number)[]
                                      min?: number
                                      max?: number
                                    }>
                                  }
                                ).filters
                              }
                            />
                          )
                          const isCollapsed =
                            collapsedChart?.messageUuid === message.uuid &&
                            collapsedChart?.chartKey === String(partIndex)
                          return isCollapsed ? (
                            <ResourceCard
                              variant="accordion"
                              icon="chart"
                              title="Chart"
                              subtitle="Chart"
                              defaultExpanded={false}
                            >
                              {legacyChart}
                            </ResourceCard>
                          ) : (
                            legacyChart
                          )
                        })()}
                      </ChartContainer>
                    )}
          </React.Fragment>
        ))}
        {message.artifacts?.ask_user_question && (
          <AskUserInputWidget
            artifact={message.artifacts.ask_user_question}
            onSubmit={isSharedView ? undefined : onAskUserQuestionSubmit}
            disabled={isSharedView || !isLatestMessage}
          />
        )}
        {showActions && !isSharedView && (
          <MessageActionsWithFeedback
            message={message}
            index={messageIndex}
            projectId={projectId}
            conversationUuid={conversationUuid}
            createTime={message.create_time}
            onExportConversation={onExportConversation}
          />
        )}
      </MessageContentWrapper>
    )
  },
)
MessageContent.displayName = 'MessageContent'

export default MessageContent
